import { Image } from 'components/Image/Image';
import { BookingStar } from 'components/pages/Finished/Reviews/BookingStart';
import { useOffice } from 'lib/hooks/useOffice/useOffice';
import { useGetReviewStats } from 'lib/react-query/Queries/useGetReviewStats';

type Props = {
  year: number;
};

const REVIEW_PLATFORMS = [
  {
    path: 'new-website/static/homepage/reviews/trustpilot.svg',
    alt: 'Trustpilot',
    className: 'h-5 lg:h-8 lg:-translate-y-1',
  },
  {
    path: 'new-website/static/homepage/reviews/reviews.svg',
    alt: 'Reviews.io',
    className: 'h-4 lg:h-6',
  },
  {
    path: 'new-website/static/homepage/reviews/google.svg',
    alt: 'Google reviews',
    className: 'hidden h-4 md:block lg:h-6',
  },
  {
    path: 'new-website/static/homepage/reviews/consumer-affairs.svg',
    alt: 'ConsumerAffairs',
    className: 'h-3 -translate-y-[0.175rem] lg:h-4 lg:-translate-y-0.5',
  },
];

const GROUP_BRANDS = [
  {
    path: 'website/logo/global-work-and-travel-logo.svg',
    alt: 'Global Work & Travel',
    className: 'w-12 lg:w-20',
  },
  {
    path: 'website/logo/dealsaway-logo.svg',
    alt: 'DealsAway',
    className: 'w-20 lg:w-32',
  },
  {
    path: 'new-website/static/homepage/global-travel-cover/gtc-logo.svg',
    alt: 'Global Travel Cover',
    className: 'w-12 lg:w-20',
  },
  {
    path: 'website/logo/global-animal-welfare-fund.png',
    alt: 'Global Animal Welfare Fund',
    className: 'w-12 lg:w-20',
  },
  {
    path: 'website/logo/rescue-paws-transparent.png',
    alt: 'Rescue Paws',
    className: 'w-5 lg:w-8',
  },
];

const FALLBACK_REVIEW_STATS = {
  rating: '4.7',
  count: 12701,
};

const FOOTER_LEGAL_COPY: Record<
  string,
  {
    copyright: string;
    details?: string;
  }
> = {
  Australia: {
    copyright:
      'The Global Work & Travel Co. (Asia Pacific) Pty Ltd | All rights reserved',
    details:
      'Level 1, 55 Collins St, Melbourne, Victoria, 3000, Australia | ABN 38600280331',
  },
  Canada: {
    copyright:
      'The Global Work & Travel Co. (Asia Pacific) Pty Ltd (Australia) | All rights reserved',
    details:
      'All bookings are contracted with The Global Work & Travel Co. Inc, Level 8, 333 Seymour St, Vancouver, British Columbia, V6B 5A6, Canada',
  },
  'United Kingdom': {
    copyright:
      'The Global Work & Travel Co. (Asia Pacific) Pty Ltd (Australia) | All rights reserved',
    details:
      'All bookings are contracted with The Global Work & Travel Co. Ltd, Registered at Elman Wall, Level 8, Becket House, 36 Old Jewry, London, EC2R 8DD, UK | Company No. 08087039 | Registered in England & Wales',
  },
};

const formatRating = (rating: string) => {
  return (Math.floor(Number(rating) * 10) / 10).toFixed(1);
};

const ReviewLogo = ({
  path,
  alt,
  className,
}: {
  path: string;
  alt: string;
  className: string;
}) => {
  return (
    <div className={'flex items-center justify-center ' + className}>
      <Image
        cloud="gwatco"
        path={path}
        alt={alt}
        className="max-h-full !object-contain"
        wrapperClassName="flex items-center justify-center"
      />
    </div>
  );
};

const BrandLogo = ({
  path,
  alt,
  className,
}: {
  path: string;
  alt: string;
  className: string;
}) => {
  return (
    <div className={'mx-2 mt-2 flex items-end justify-center ' + className}>
      <Image
        cloud="gwatco"
        path={path}
        alt={alt}
        className="max-h-full !object-contain"
        wrapperClassName="flex items-end justify-center"
      />
    </div>
  );
};

const Gwat = ({ year }: Props) => {
  const office = useOffice();
  const { data: reviewStatsData } = useGetReviewStats();
  const reviewStats = reviewStatsData?.result;
  const rating = reviewStats?.rating ?? FALLBACK_REVIEW_STATS.rating;
  const reviewCount = reviewStats?.count ?? FALLBACK_REVIEW_STATS.count;
  const legalCopy =
    FOOTER_LEGAL_COPY[office.office] ?? FOOTER_LEGAL_COPY.Australia;

  return (
    <footer className="w-full border-t border-light-800 bg-light-600 text-dark-800">
      <section className="bg-light-600">
        <div className="mx-auto flex max-w-screen-lg flex-col items-center justify-between p-5 text-center md:px-0 lg:flex-row lg:gap-5">
          <div className="text-center md:text-left">
            <h3 className="mb-3 px-3 text-[1.25rem] font-bold leading-5 text-dark-900 md:mb-1 lg:mr-5 lg:px-0 lg:text-left lg:text-[1.625rem] lg:leading-[1.625rem]">
              The world's{' '}
              <span className="relative inline-block after:absolute after:bottom-[-2px] after:left-[-5px] after:h-full after:w-[128%] after:bg-[url('https://gwatco-res.cloudinary.com/image/upload/v1680056080/new-website/static/homepage/stroke.svg')] after:bg-bottom after:bg-no-repeat after:content-['']">
                best
              </span>{' '}
              working holidays.
            </h3>
            <div className="flex flex-row items-center justify-center gap-2 text-sm font-medium leading-6 text-dark-900 md:justify-start md:text-base">
              <span className="h-6 w-[2.625rem] rounded bg-[#FED141] text-sm font-bold leading-6 text-dark-900 md:hidden">
                {formatRating(rating)}
              </span>
              <span className="hidden md:block">{formatRating(rating)}</span>
              <BookingStar
                rating={Number(rating)}
                size={0.9}
                spacingMultiplier={0.8}
              />
              <span>
                rating from over {reviewCount.toLocaleString()} reviews
              </span>
            </div>
          </div>
          <div className="flex items-end gap-5 pt-3 lg:items-center lg:p-0">
            {REVIEW_PLATFORMS.map((platform) => (
              <ReviewLogo key={platform.alt} {...platform} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-light-700 py-5 text-center">
        <div className="px-5">
          <div className="text-lg font-semibold leading-6 text-dark-800">
            The Global Work & Travel Group
          </div>
          <p className="mx-auto mt-2 max-w-3xl text-sm font-semibold leading-5 text-dark-600">
            Proudly part of the Global Work & Travel family of brands, a
            worldwide leader in life-changing travel & technology.
          </p>
          <div className="mt-2 flex flex-wrap items-end justify-center">
            {GROUP_BRANDS.map((brand) => (
              <BrandLogo key={brand.alt} {...brand} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-light-600 px-5 py-5 text-center text-sm font-normal leading-[22px] text-dark-800">
        <div className="mx-auto flex flex-col items-center justify-center md:max-w-[42rem]">
          <span>
            Copyright © {year} {legalCopy.copyright}
          </span>
          {legalCopy.details ? (
            <span className="mt-1">{legalCopy.details}</span>
          ) : null}
        </div>
      </section>
    </footer>
  );
};

export { Gwat };
